import React, { useState, useEffect } from "react"
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Mail,
  Clock,
  Calendar,
  ShieldAlert,
  MapPin
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { INSTITUTES } from "../constants"

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const isInstitutesActive =
    location.pathname.includes("/institutes") ||
    location.pathname.includes("/institute/")

  return (
    <header className="fixed top-0 left-0 w-full z-[100] transition-all duration-300">

      {/* -------- TOP UTILITY BAR (DESKTOP ONLY) -------- */}
      <div
        className={`hidden lg:block bg-[#f8f9fa] border-b border-gray-100 transition-all duration-500 ease-in-out ${
          scrolled
            ? "max-h-0 opacity-0 py-0 overflow-hidden border-none"
            : "max-h-20 opacity-100 py-2"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-[11px] font-bold text-gray-500 uppercase tracking-widest">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2 group cursor-pointer">
              <Phone
                size={14}
                className="text-vcnr-brand group-hover:rotate-12 transition-transform"
              />
              <span className="group-hover:text-vcnr-brand transition-colors">
                24/7 Help: +91 866 018 2095
              </span>
            </div>
            <div className="flex items-center gap-2 group cursor-pointer">
              <Mail
                size={14}
                className="text-vcnr-brand group-hover:-translate-y-1 transition-transform"
              />
              <span className="group-hover:text-vcnr-brand transition-colors">
                info@vcnrhealth.com
              </span>
            </div>
          </div>

          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2 group cursor-pointer">
              <MapPin
                size={14}
                className="text-vcnr-brand group-hover:scale-110 transition-transform"
              />
              <span className="group-hover:text-vcnr-brand transition-colors">
                Bangalore Central Hub
              </span>
            </div>
            <div className="flex items-center gap-2 text-red-600 group cursor-pointer">
              <ShieldAlert size={14} className="animate-pulse" />
              <span className="group-hover:text-red-700 transition-colors">
                Emergency Hotline: 1066
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* -------- MAIN NAVBAR -------- */}
      <nav
        className={`w-full bg-white transition-all duration-500 ${
          scrolled ? "shadow-xl py-3" : "shadow-sm py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center">

          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="https://vcnrgroup.com/static/media/logo.a248d5f0fd9abb2a75015071670e6671.svg"
              alt="VCNR Hospital Logo"
              className={`transition-all duration-500 group-hover:scale-105 ${
                scrolled ? "h-9 sm:h-10" : "h-10 sm:h-12"
              } w-auto`}
            />
          </Link>

          {/* -------- DESKTOP NAV -------- */}
          <div className="hidden lg:flex items-center gap-8 xl:gap-10">

            <Link
              to="/"
              className={`nav-link-ltr text-[14px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                location.pathname === "/"
                  ? "text-vcnr-brand active"
                  : "text-gray-700 hover:text-vcnr-brand"
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`nav-link-ltr text-[14px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                location.pathname === "/about"
                  ? "text-vcnr-brand active"
                  : "text-gray-700 hover:text-vcnr-brand"
              }`}
            >
              About Us
            </Link>

            {/* Institutes Dropdown */}
            <div
              className="relative group h-full flex items-center"
              onMouseEnter={() => setActiveDropdown(true)}
              onMouseLeave={() => setActiveDropdown(false)}
            >
              <Link
                to="/institutes"
                className={`nav-link-ltr text-[14px] font-semibold uppercase tracking-[0.1em] flex items-center gap-1 transition-colors ${
                  isInstitutesActive
                    ? "text-vcnr-brand active"
                    : "text-gray-700 hover:text-vcnr-brand"
                }`}
              >
                Institutes{" "}
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${
                    activeDropdown ? "rotate-180" : ""
                  }`}
                />
              </Link>

              <div
                className={`absolute top-full left-1/2 -translate-x-1/2 w-[640px] bg-white shadow-2xl rounded-2xl border border-gray-100 p-8 transition-all duration-500 mt-2 grid grid-cols-2 gap-x-8 gap-y-4 ${
                  activeDropdown
                    ? "opacity-100 translate-y-0 visible scale-100"
                    : "opacity-0 translate-y-4 invisible scale-95"
                }`}
              >
                {INSTITUTES.map(inst => (
                  <Link
                    key={inst.id}
                    to={`/institute/${inst.id}`}
                    className="flex items-start gap-4 p-3 rounded-xl hover:bg-vcnr-brand/5 group/item transition-all hover:scale-105"
                    onClick={() => setActiveDropdown(false)}
                  >
                    <div className="p-2 bg-vcnr-brand/10 text-vcnr-brand rounded-lg group-hover/item:bg-vcnr-brand group-hover/item:text-white transition-colors group-hover/item:rotate-12">
                      <Clock size={16} />
                    </div>
                    <div>
                      <div className="text-[14px] font-semibold uppercase tracking-tight text-gray-900 mb-0.5 group-hover/item:text-vcnr-brand transition-colors">
                        {inst.name.replace("VCNR ", "")}
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest line-clamp-1">
                        Expert clinical care
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/doctors"
              className={`nav-link-ltr text-[14px] font-semibold uppercase tracking-[0.1em] transition-colors ${
                location.pathname === "/doctors"
                  ? "text-vcnr-brand active"
                  : "text-gray-700 hover:text-vcnr-brand"
              }`}
            >
              Specialists
            </Link>

            <Link
              to="/booking"
              className="bg-vcnr-brand text-white px-6 xl:px-8 py-3 xl:py-4 rounded-xl text-[12px] font-semibold uppercase tracking-[0.15em] shadow-lg shadow-vcnr-brand/20 hover:bg-black hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3"
            >
              <Calendar size={16} className="animate-pulse" />
              Book Appointment
            </Link>
          </div>

          {/* -------- MOBILE MENU BUTTON -------- */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-vcnr-brand transition-all"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* -------- MOBILE DRAWER -------- */}
      <div
        className={`fixed inset-0 bg-white z-[200] lg:hidden transition-transform duration-500 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="p-6 sm:p-8 flex flex-col h-full">

          <div className="flex justify-between items-center mb-10">
            <img
              src="https://vcnrgroup.com/static/media/logo.a248d5f0fd9abb2a75015071670e6671.svg"
              className="h-9 sm:h-10"
              alt="VCNR"
            />
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 bg-gray-100 rounded-full hover:rotate-90 transition-transform"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex flex-col gap-6 flex-1 overflow-y-auto">

            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="text-2xl sm:text-3xl font-semibold uppercase tracking-tighter text-gray-900 border-b border-gray-100 pb-3 hover:text-vcnr-brand transition-colors"
            >
              Home
            </Link>

            <Link
              to="/about"
              onClick={() => setIsOpen(false)}
              className="text-2xl sm:text-3xl font-semibold uppercase tracking-tighter text-gray-900 border-b border-gray-100 pb-3 hover:text-vcnr-brand transition-colors"
            >
              About Us
            </Link>

            <Link
              to="/institutes"
              onClick={() => setIsOpen(false)}
              className="text-2xl sm:text-3xl font-semibold uppercase tracking-tighter text-gray-900 border-b border-gray-100 pb-3 hover:text-vcnr-brand transition-colors"
            >
              Institutes
            </Link>

            <Link
              to="/doctors"
              onClick={() => setIsOpen(false)}
              className="text-2xl sm:text-3xl font-semibold uppercase tracking-tighter text-gray-900 border-b border-gray-100 pb-3 hover:text-vcnr-brand transition-colors"
            >
              Specialists
            </Link>

            <Link
              to="/booking"
              onClick={() => setIsOpen(false)}
              className="text-2xl sm:text-3xl font-semibold uppercase tracking-tighter text-vcnr-brand border-b border-gray-100 pb-3 hover:scale-105 origin-left transition-transform"
            >
              Booking
            </Link>
          </div>

          {/* Emergency Button */}
          <div className="pt-6 border-t border-gray-100">
            <a
              href="tel:1066"
              className="w-full py-4 bg-red-600 text-white rounded-2xl flex items-center justify-center gap-3 text-xs sm:text-sm font-semibold uppercase tracking-widest hover:bg-red-700 hover:scale-105 active:scale-95 transition-all"
            >
              <ShieldAlert size={18} className="animate-pulse" />
              Emergency Support: 1066
            </a>
          </div>

        </div>
      </div>
    </header>
  )
}

export default Navbar
